import { createContext, useContext, useMemo, useReducer } from 'react';

/**
 * AppContext — top-level state shared across the mobile shell.
 * -------------------------------------------------------------------------
 * Scope decisions (so the team knows what belongs here vs. screen-local):
 *
 *   ✓ Active tab — needed by both the BottomTabBar and the ScreenRouter
 *   ✓ Last-tab direction — needed by the screen-transition wrapper to pick
 *       the slide direction
 *   ✓ Cross-screen events (e.g. "open Oracle with this prompt seeded") —
 *       this is the kind of glue that screen-local state can't handle
 *
 *   ✗ News data, results data — screen-local. Each screen owns its own
 *       lifecycle. Bringing them here would couple unrelated screens and
 *       force re-renders on every fetch.
 *   ✗ Oracle chat history — same reason. The Oracle screen owns it.
 *
 * Reducer pattern is used (instead of plain useState) because the dispatch
 * surface is small, multi-action, and benefits from being statically named
 * so analytics can trace tab navigation without instrumenting every caller.
 */

const TABS = ['news', 'results', 'oracle'];

const initialState = {
  activeTab: 'news',
  previousTab: null,
  // Optional payload passed when one screen wants to seed another.
  // Example: tap "Ask the Oracle" inside a news card → seed Oracle with the headline.
  pendingOraclePrompt: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_ACTIVE_TAB': {
      if (!TABS.includes(action.payload)) return state;
      if (state.activeTab === action.payload) return state;
      return {
        ...state,
        previousTab: state.activeTab,
        activeTab: action.payload,
      };
    }
    case 'SEED_ORACLE_PROMPT': {
      return {
        ...state,
        previousTab: state.activeTab,
        activeTab: 'oracle',
        pendingOraclePrompt: action.payload,
      };
    }
    case 'CONSUME_ORACLE_PROMPT': {
      if (!state.pendingOraclePrompt) return state;
      return { ...state, pendingOraclePrompt: null };
    }
    default:
      return state;
  }
}

const AppStateContext = createContext(null);
const AppDispatchContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  /**
   * Memoised action creators — keeping these stable lets consumers depend on
   * them without triggering unnecessary effect re-runs.
   */
  const actions = useMemo(
    () => ({
      setActiveTab: (tab) => dispatch({ type: 'SET_ACTIVE_TAB', payload: tab }),
      seedOraclePrompt: (prompt) => dispatch({ type: 'SEED_ORACLE_PROMPT', payload: prompt }),
      consumeOraclePrompt: () => dispatch({ type: 'CONSUME_ORACLE_PROMPT' }),
    }),
    []
  );

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={actions}>{children}</AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

/** Hook — read the shell state. Throws if used outside the provider. */
export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (ctx == null) {
    throw new Error('useAppState must be used inside <AppProvider>');
  }
  return ctx;
}

/** Hook — read the action creators. Same provider guard as above. */
export function useAppActions() {
  const ctx = useContext(AppDispatchContext);
  if (ctx == null) {
    throw new Error('useAppActions must be used inside <AppProvider>');
  }
  return ctx;
}

/**
 * Direction of the current transition — used by ScreenContainer to pick the
 * slide direction. Returns 1 if the new tab is to the right of the previous,
 * -1 if to the left, 0 on first paint.
 */
export function useTransitionDirection() {
  const { activeTab, previousTab } = useAppState();
  return useMemo(() => {
    if (!previousTab) return 0;
    const current = TABS.indexOf(activeTab);
    const previous = TABS.indexOf(previousTab);
    return Math.sign(current - previous);
  }, [activeTab, previousTab]);
}

export const TAB_IDS = TABS;
